import { dsDisabled } from "@/config";
import { makeFarmClient } from "@/lib/farm-client";
import { queryClient } from "@/lib/query-client";
import { DSFarmAprMap, DSFarmStateExtended } from "@/lib/types";
import { FarmStateAndAPR } from "@myth-finance/dualstake-farm-sdk";
import { AprBreakdown } from "@myth-finance/dualstake-farm-sdk/dist/DualstakeFarmClient";
import { DSContractListing, DSContractState } from "@myth-finance/dualstake-ts-sdk";
import { useQuery } from "@tanstack/react-query";
import { postProcessAprBreakdown } from "./use-apr-breakdown";

function pickAprBreakdown(data: FarmStateAndAPR): AprBreakdown {
  const {
    balance,
    staked,
    currentBlockBonus,
    currentAvgBlockPayout,
    currentFarmAmount,
    currentFarmAmountAlgo,
    overrideFarmAmount,
    overrideFarmAmountAlgo,
    avgRoundTime,
    onlineStake,
    expectedYearlyBlocks,
    baseAprBps,
    farmAprBps,
    overrideFarmAprBps,
  } = data;
  return {
    balance,
    staked,
    currentBlockBonus,
    currentAvgBlockPayout,
    currentFarmAmount,
    currentFarmAmountAlgo,
    overrideFarmAmount,
    overrideFarmAmountAlgo,
    avgRoundTime,
    onlineStake,
    expectedYearlyBlocks,
    baseAprBps,
    farmAprBps,
    overrideFarmAprBps,
  };
}

function pickFarm(data: FarmStateAndAPR): DSFarmStateExtended | undefined {
  if (data.farmAsset) {
    const {
      appId,
      appEscrow,
      farmAsset,
      amountPerBlock,
      remainingDurationBlocks,
      remainingDurationSec,
      lastBlockPaid,
    } = data as FarmStateAndAPR;
    return {
      appId,
      appEscrow,
      farmAsset,
      amountPerBlock,
      remainingDurationBlocks,
      remainingDurationSec,
      lastBlockPaid,
    };
  }
}

export const useFarmsAndAPRs = (cStates: (DSContractListing | DSContractState)[]) => {
  const appIds = cStates.map(({ appId }) => appId);
  const queryKeyId = appIds.sort().reduce((str, appId) => `${str}-${appId}`, "");
  return useQuery({
    queryKey: [
      "farms-and-aprs",
      queryKeyId,
    ],
    enabled: !!cStates?.length && !!cStates[0].appId,
    staleTime: 60 * 1_000,
    queryFn: async (): Promise<DSFarmAprMap> => {
      if (dsDisabled || !cStates.length) return new Map();
      console.debug("Requesting", cStates.length, "farms and aprs", queryKeyId);
      try {
        const outData: DSFarmAprMap = new Map();
        const inData = await makeFarmClient().getFarmsAndAPR(appIds);
        for (const [appId, farmAndApr] of inData.entries()) {
          const cState = cStates.find(({ appId: a }) => a === appId);
          if (!cState) {
            throw new Error(`While parsing farms+apr: ${appId} not found in cStates. This should never happen, please report.`)
          }
          const aprBreakdown = pickAprBreakdown(farmAndApr);
          const fullAprBreakdown = postProcessAprBreakdown(cState, aprBreakdown);
          const farm = pickFarm(farmAndApr);
          const value = { apr: fullAprBreakdown, farm }
          outData.set(appId, value)
        }
        return outData
      } catch (e) {
        console.error(e)
        throw new Error(`While loading farms: ${(e as Error)?.message ?? e}`);
      }
    },
  });
};

export const clearFarmsAndAPRCache = () => {
  queryClient.invalidateQueries({ queryKey: ["farms-and-aprs"] });
};
